import { useEffect, useRef, useState } from "react"
import { Head, Link, router, useForm, usePoll } from "@inertiajs/react"
import { AlertTriangle, ArrowLeft, Check, Loader2, Sparkles } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { BookSectionEditor } from "./components/BookSectionEditor"
import { BrandCheckCard } from "./components/BrandCheckCard"
import { useFlagHighlights } from "@/hooks/use-flag-highlights"

// "A Few Words from the Author": the AI drafts it from the chapters, the
// coach edits and approves it. The page adds "Dear Friend," and the signature.
export default function BookLetter({ currentUser, book, letter, signature, brand }) {
  const { start, stop } = usePoll(5000, { only: ["letter"] }, { autoStart: false })
  useEffect(() => {
    if (letter.drafting) start()
    else stop()
    return () => stop()
  }, [letter.drafting])

  const form = useForm({ author_letter: letter.body || "", author_contact: letter.contact || "" })
  const [editorKey, setEditorKey] = useState(0)
  useEffect(() => {
    form.setData("author_letter", letter.body || "")
    setEditorKey((key) => key + 1)
  }, [letter.body])

  // The Brand check's flagged words, highlighted in the letter (and its editor).
  const page = useRef(null)
  const hasLetter = Boolean(letter.body || form.data.author_letter)
  const locate = useFlagHighlights(page, brand.flags, { watch: hasLetter })

  const save = () => {
    form.transform((data) => ({ book: data }))
    form.patch(`/app/coach/books/${book.id}`, { preserveScroll: true })
  }
  const changed = form.data.author_letter !== (letter.body || "") || form.data.author_contact !== (letter.contact || "")
  const locked = book.sentToPrint

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`Author letter - ${book.title}`} />
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <Link href={`/app/coach/books/${book.id}`} className="rounded-md p-2 hover:bg-zinc-100" aria-label="Back to the book">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">{book.title}</div>
            <h1 className="text-2xl font-bold text-zinc-900">A Few Words from the Author</h1>
            <p className="text-zinc-600">The letter that opens your book. The AI drafts it from your chapters. You edit it and approve it.</p>
          </div>
          {letter.approved && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Approved</span>}
        </div>

        {letter.drafting && (
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <Loader2 className="h-4 w-4 animate-spin" /> The AI is writing your letter. It takes about a minute.
          </div>
        )}
        {letter.error && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <AlertTriangle className="h-4 w-4" /> {letter.error}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <Card>
            <CardContent className="space-y-4">
              {hasLetter ? (
                <div ref={page} className="space-y-4">
                  <p className="kf-book-page">Dear Friend,</p>
                  {locked ? (
                    <div className="kf-book-page" dangerouslySetInnerHTML={{ __html: letter.body }} />
                  ) : (
                    <BookSectionEditor key={editorKey} letter value={form.data.author_letter} onChange={(html) => form.setData("author_letter", html)} />
                  )}
                  <div className="kf-book-page">
                    <p className="mb-0">With gratitude,</p>
                    <p className="mb-0 font-semibold">{signature}</p>
                    <p className="whitespace-pre-line text-sm text-zinc-600">{form.data.author_contact}</p>
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center text-sm text-zinc-600">
                  No letter yet. {letter.canDraft ? "Ask the AI to draft it from your chapters." : "Draft at least one chapter first."}
                </div>
              )}
              {letter.warnings.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <b>The automatic check found:</b> {letter.warnings.join("; ")}.
                </div>
              )}
            </CardContent>
          </Card>

          {!locked && (
            <aside className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Your contact lines</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="contact" className="sr-only">Contact lines</Label>
                  <Textarea id="contact" rows={4} value={form.data.author_contact} onChange={(e) => form.setData("author_contact", e.target.value)}
                    placeholder={"Author, Kingdom Factors of Indy\nCoach, Kingdom Factor\nyou@kingdomfactor.us"} />
                  <p className="text-xs text-zinc-500">Printed under your name at the end of the letter.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="space-y-3">
                  <Button className="w-full" onClick={save} disabled={!changed || form.processing || letter.drafting}>Save changes</Button>
                  <Button className="w-full" variant="outline" disabled={!letter.body || changed || letter.approved || letter.drafting}
                    onClick={() => router.post(`/app/coach/books/${book.id}/approve_letter`)}>
                    <Check className="h-4 w-4" /> Approve letter
                  </Button>
                  <Button className="w-full" variant="ghost" disabled={!letter.canDraft || letter.drafting}
                    onClick={() => {
                      if (!letter.body || window.confirm("Replace this letter with a new draft from the AI?")) {
                        router.post(`/app/coach/books/${book.id}/draft_letter`)
                      }
                    }}>
                    <Sparkles className="h-4 w-4" /> {letter.body ? "Draft it again with the AI" : "Draft it with the AI"}
                  </Button>
                  {changed && <p className="text-xs text-zinc-500">Save your changes before you approve.</p>}
                </CardContent>
              </Card>
              <BrandCheckCard bookId={book.id} brand={brand} locked={locked} locate={locate} showText="Show in the letter"
                notCheckedText="Runs when you run the Brand check from My Book, and again when you assemble the book."
                fixText="Or change the words above and save." />
            </aside>
          )}
        </div>
      </div>
    </CoachLayout>
  )
}
